/**
 * Everything the application needs in scope, in the order it needs it.
 *
 * The order is not arbitrary and is the kind of thing that costs an afternoon
 * when it is wrong:
 *
 *   Query     — the session is a query, so it has to be outside the session.
 *   Appearance— reads nothing, but writes to <html>, so it should be high and
 *               mounted once.
 *   Session   — the shell, the router guard and most pages read it.
 *   Tooltip   — Radix's provider must enclose every tooltip trigger.
 *   Toast     — the viewport is fixed-position, so where it sits in the tree
 *               does not matter visually, but `useToast` must resolve from any
 *               page.
 *   Palette   — registers commands from the shell and from pages, so it has to
 *               enclose both.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { retryDelay, retryPolicy } from '~/api/queries';
import { SessionProvider } from '~/auth/session';
import { CommandPaletteProvider, ToastProvider, TooltipProvider } from '~/components';
import { AppearanceProvider } from './appearance';

/**
 * Created at module scope, deliberately.
 *
 * A client created inside the component is a new cache on every render of the
 * tree, which works in development and throws away every cached page on a hot
 * reload — the classic version of this bug. There is no server rendering here
 * (ADR-0003 rejected SSR), so there is no request to scope a client to.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryPolicy,
      retryDelay,
      // Off. The default refetches every query when the tab regains focus,
      // which for a user who alternates between an editor and this tab all day
      // is a request storm for data that has a stale time anyway. Each query
      // states its own freshness; that is the knob.
      refetchOnWindowFocus: false,
      // On. Coming back from offline is the case where the cache is genuinely
      // suspect.
      refetchOnReconnect: true,
    },
    mutations: {
      // A mutation is not idempotent unless the endpoint says so. Retrying one
      // automatically is how a single click becomes three writes.
      retry: false,
    },
  },
});

export function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <AppearanceProvider>
        <SessionProvider>
          <TooltipProvider>
            <ToastProvider>
              <CommandPaletteProvider>{children}</CommandPaletteProvider>
            </ToastProvider>
          </TooltipProvider>
        </SessionProvider>
      </AppearanceProvider>
    </QueryClientProvider>
  );
}
